import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fetchProposalDropdownOptions } from "../OrderService";

const DROPDOWN_OPTIONS_STALE_TIME = 30_000;

// RULES_JSON's dependent-fetch params are resolved to a flat object by the
// time fetchOptions sees them; by convention the static owner-entityType
// literal is aliased "parentType" and the TARGET field's own entityType
// (what kind of options this fetch resolves, e.g. "CTY") is aliased "type"
// (see JSON.js's "City depends on State" rules) — whatever other key is
// present is the dynamic value coming from the parent field itself. This
// holds for any future dependent rule that follows the same authoring
// convention — no per-type branch needed here. Warns instead of guessing if
// a rule ever supplies more than one candidate key, since silently picking
// one would be wrong data with no visible signal.
const getOwnerValueFromParams = (params = {}) => {
  const candidateKeys = Object.keys(params).filter(
    (key) => key !== "parentType" && key !== "type"
  );
  if (candidateKeys.length > 1) {
    console.warn(
      "useCraftDataProvider: ambiguous dependent-fetch params, expected exactly one non-parentType/type key",
      params
    );
  }
  return candidateKeys.length ? params[candidateKeys[0]] : undefined;
};

// Builds the CraftDataProvider the renderer calls into for every select's
// options — both independent ones (fetchEntityOptions, fired on mount) and
// rule-driven dependent ones (fetchOptions, fired once the rules engine
// resolves a fetch task for it). `agId` scopes the dropdown list the same
// way CustomSelect.jsx does today (parentType "AGRP" + the AG's entityid).
const useCraftDataProvider = (agId) => {
  const queryClient = useQueryClient();

  return useMemo(
    () => ({
      fetchEntityOptions: ({ entityType }) =>
        queryClient.fetchQuery({
          queryKey: ["proposal-dropdown-options", entityType],
          queryFn: () =>
            fetchProposalDropdownOptions({ type: entityType, agId }),
          staleTime: DROPDOWN_OPTIONS_STALE_TIME,
        }),
      fetchOptions: (task) => {
        const type = task.params?.type;
        const ownerType = task.params?.parentType;
        const ownerValue = getOwnerValueFromParams(task.params);

        if (!type) {
          // Fails loud instead of silently posting type="undefined" to the
          // backend — the rule authoring the fetch must supply a
          // {value: "<TYPE>", as: "type"} param (see JSON.js) so this stays
          // generic across every dependent-select type, not just city.
          throw new Error(
            `useCraftDataProvider: rule for "${task.targetName}" is missing a {value: "<TYPE>", as: "type"} param in fetchOptions.params.`
          );
        }

        return queryClient.fetchQuery({
          queryKey: ["proposal-dropdown-options", type, ownerType, ownerValue],
          queryFn: () =>
            fetchProposalDropdownOptions({ type, agId, ownerType, ownerValue }),
          staleTime: DROPDOWN_OPTIONS_STALE_TIME,
        });
      },
    }),
    [queryClient, agId]
  );
};

export default useCraftDataProvider;
